import React, { useState, useEffect } from 'react';
import { Share2, Instagram, Facebook, FileText, Check, AlertCircle, Calendar, Send, Sparkles, Eye, Trash2, Clock, RefreshCw, ChevronLeft, ChevronRight, Plus, X, Image, CheckCircle, Video, Film, Play, Download, Edit2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Patologie e argomenti per i post
const PATOLOGIE = [
  { id: 'dry_eye', name: 'Sindrome Occhio Secco (Dry Eye)', icon: '💧' },
  { id: 'pterygium', name: 'Pterigio', icon: '👁️' },
  { id: 'cataract', name: 'Cataratta', icon: '🔍' },
  { id: 'glaucoma', name: 'Glaucoma', icon: '⚠️' },
  { id: 'maculopathy', name: 'Maculopatia', icon: '🎯' },
  { id: 'myopia', name: 'Miopia', icon: '👓' },
  { id: 'astigmatism', name: 'Astigmatismo', icon: '📐' },
  { id: 'presbyopia', name: 'Presbiopia', icon: '📖' },
  { id: 'keratoconus', name: 'Cheratocono', icon: '🔬' },
  { id: 'diabetic_retinopathy', name: 'Retinopatia Diabetica', icon: '🩸' },
];

const STUDIO_TOPICS = [
  { id: 'visit', name: 'Visita PRIME VISION', icon: '🏥' },
  { id: 'technology', name: 'Tecnologia Avanzata', icon: '🔬' },
  { id: 'team', name: 'Il Nostro Team', icon: '👥' },
  { id: 'prevention', name: 'Prevenzione Oculare', icon: '🛡️' },
];

const SocialMediaManager = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('monthly');
  
  // Piano mensile
  const [monthlyPlan, setMonthlyPlan] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Generazione
  const [generating, setGenerating] = useState({});
  const [publishing, setPublishing] = useState({});
  
  // Modal
  const [editingPost, setEditingPost] = useState(null);
  const [previewPost, setPreviewPost] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadMonthlyPlan();
  }, [currentMonth]);

  const loadMonthlyPlan = async () => {
    setLoadingPlan(true);
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await axios.get(`${API}/api/social/monthly-plan?month=${month}&year=${year}`, { headers });
      setMonthlyPlan(res.data.posts || []);
    } catch (error) {
      console.error('Error loading monthly plan:', error);
      // Se non esiste, genera automaticamente
      if (error.response?.status === 404) {
        generateMonthlyPlan();
      }
    } finally {
      setLoadingPlan(false);
      setLoading(false);
    }
  };

  const generateMonthlyPlan = async () => {
    setLoadingPlan(true);
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      const res = await axios.post(`${API}/api/social/generate-monthly-plan`, {
        month, year
      }, { headers });
      
      if (res.data.success) {
        toast.success(`Piano mensile generato: ${res.data.posts_created} post`);
        loadMonthlyPlan();
      } else {
        toast.error(res.data.error || 'Errore generazione piano');
      }
    } catch (error) {
      toast.error('Errore generazione piano mensile');
    } finally {
      setLoadingPlan(false);
    }
  };

  const generateImage = async (postId) => {
    setGenerating(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await axios.post(`${API}/api/social/generate-image/${postId}`, {}, { headers });
      
      if (res.data.success) {
        toast.success('Immagine generata con logo PRIME VISION!');
        loadMonthlyPlan();
      } else {
        toast.error(res.data.error || 'Errore generazione immagine');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione immagine');
    } finally {
      setGenerating(prev => ({ ...prev, [postId]: false }));
    }
  };

  const publishPost = async (postId, platform = 'instagram') => {
    setPublishing(prev => ({ ...prev, [postId]: platform }));
    try {
      const res = await axios.post(`${API}/api/social/publish-now/${postId}`, {
        platform
      }, { headers });
      
      if (res.data.success) {
        toast.success(`Pubblicato su ${platform}!`);
        loadMonthlyPlan();
      } else {
        toast.error(res.data.error || 'Errore pubblicazione');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore pubblicazione');
    } finally {
      setPublishing(prev => ({ ...prev, [postId]: null }));
    }
  };

  const updatePost = async (postId, updates) => {
    try {
      const res = await axios.patch(`${API}/api/social/post/${postId}`, updates, { headers });
      
      if (res.data.success) {
        toast.success('Post aggiornato');
        loadMonthlyPlan();
        setEditingPost(null);
      } else {
        toast.error(res.data.error || 'Errore aggiornamento');
      }
    } catch (error) {
      toast.error('Errore aggiornamento post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Eliminare questo post?')) return;
    
    try {
      await axios.delete(`${API}/api/social/post/${postId}`, { headers });
      toast.success('Post eliminato');
      loadMonthlyPlan();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-700';
      case 'ready': return 'bg-blue-100 text-blue-700';
      case 'scheduled': return 'bg-purple-100 text-purple-700';
      case 'planned': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'published': return 'Pubblicato';
      case 'ready': return 'Pronto';
      case 'scheduled': return 'Programmato';
      case 'planned': return 'Pianificato';
      default: return status;
    }
  };

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin" style={{color: '#2A9FCB'}} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Share2 className="w-8 h-8" style={{color: '#2A9FCB'}} />
          <div>
            <h2 className="text-2xl font-bold" style={{color: '#2A9FCB'}}>Social Media Manager</h2>
            <p className="text-gray-500 text-sm">Gestisci Post e Reel per Instagram e Facebook</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <span className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-gradient-to-r from-pink-100 to-purple-100 text-pink-700">
            <Instagram className="w-4 h-4" />
            Instagram
          </span>
          <span className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-700">
            <Facebook className="w-4 h-4" />
            Facebook
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b overflow-x-auto">
        {[
          { id: 'monthly', label: 'Piano Mensile', icon: Calendar },
          { id: 'create', label: 'Crea Nuovo', icon: Plus },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'border-sky-500 text-sky-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Piano Mensile */}
      {activeTab === 'monthly' && (
        <div className="space-y-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border">
            <button 
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <div className="text-center">
              <h3 className="text-xl font-bold" style={{color: '#2A9FCB'}}>
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <p className="text-sm text-gray-500">{monthlyPlan.length} post pianificati</p>
            </div>
            
            <button 
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Generate Plan Button */}
          {monthlyPlan.length === 0 && (
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-8 text-center border border-sky-100">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-sky-400" />
              <h3 className="text-xl font-bold mb-2" style={{color: '#2A9FCB'}}>Nessun Piano per questo Mese</h3>
              <p className="text-gray-600 mb-6">Genera automaticamente un piano con 3 post patologie + 1 post studio a settimana</p>
              <button
                onClick={generateMonthlyPlan}
                disabled={loadingPlan}
                data-testid="generate-monthly-plan-btn"
                className="px-6 py-3 rounded-xl text-white font-medium flex items-center gap-2 mx-auto disabled:opacity-50"
                style={{background: 'linear-gradient(135deg, #2A9FCB, #1e7a9c)'}}
              >
                {loadingPlan ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                Genera Piano Mensile
              </button>
            </div>
          )}

          {/* Post List */}
          {monthlyPlan.length > 0 && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border">
                  <p className="text-sm text-gray-500">Totale</p>
                  <p className="text-2xl font-bold" style={{color: '#2A9FCB'}}>{monthlyPlan.length}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <p className="text-sm text-green-600">Pubblicati</p>
                  <p className="text-2xl font-bold text-green-700">
                    {monthlyPlan.filter(p => p.status === 'published').length}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-sm text-blue-600">Pronti</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {monthlyPlan.filter(p => p.status === 'ready').length}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                  <p className="text-sm text-amber-600">Da Preparare</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {monthlyPlan.filter(p => p.status === 'planned').length}
                  </p>
                </div>
              </div>

              {/* Refresh button */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={loadMonthlyPlan}
                  data-testid="refresh-plan-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingPlan ? 'animate-spin' : ''}`} />
                  Aggiorna
                </button>
                <button
                  onClick={generateMonthlyPlan}
                  disabled={loadingPlan}
                  data-testid="add-post-btn"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50"
                  style={{background: '#2A9FCB'}}
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi Post
                </button>
              </div>

              {/* Post Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {monthlyPlan.map(post => (
                  <div 
                    key={post.id} 
                    className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
                      post.status === 'published' ? 'opacity-75' : ''
                    }`}
                  >
                    {/* Image Preview */}
                    <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200">
                      {post.image_url || post.public_url ? (
                        <img 
                          src={post.public_url || (post.image_url?.startsWith('http') ? post.image_url : `${API}${post.image_url}`)}
                          alt={post.topic_title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`absolute inset-0 flex items-center justify-center ${post.image_url || post.public_url ? 'hidden' : ''}`}
                        style={{display: post.image_url || post.public_url ? 'none' : 'flex'}}
                      >
                        <div className="text-center">
                          <Image className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                          <button
                            onClick={() => generateImage(post.id)}
                            disabled={generating[post.id]}
                            data-testid={`generate-image-btn-${post.id}`}
                            className="px-4 py-2 bg-white rounded-lg shadow-sm text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
                            style={{color: '#2A9FCB'}}
                          >
                            {generating[post.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                            Genera Immagine
                          </button>
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <div className="absolute top-2 right-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                          {getStatusLabel(post.status)}
                        </span>
                      </div>
                      
                      {/* Type Badge */}
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          post.type === 'reel' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'
                        }`}>
                          {post.type === 'reel' ? '🎬 Reel' : '📷 Post'}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-800 line-clamp-1">{post.topic_title}</h4>
                      </div>
                      
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {post.caption?.substring(0, 100)}...
                      </p>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                        <Clock className="w-3 h-3" />
                        {post.scheduled_date ? new Date(post.scheduled_date).toLocaleDateString('it-IT', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        }) : 'Non programmato'}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        {post.status !== 'published' && (
                          <>
                            <button
                              onClick={() => setPreviewPost(post)}
                              data-testid={`preview-btn-${post.id}`}
                              className="flex-1 py-2 px-3 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 flex items-center justify-center gap-1"
                            >
                              <Eye className="w-4 h-4" />
                              Anteprima
                            </button>
                            
                            {(post.image_url || post.public_url) && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() => publishPost(post.id, 'instagram')}
                                  disabled={publishing[post.id]}
                                  data-testid={`publish-instagram-btn-${post.id}`}
                                  className="py-2 px-3 rounded-lg text-sm font-medium text-white flex items-center gap-1 disabled:opacity-50"
                                  style={{background: 'linear-gradient(135deg, #E1306C, #C13584)'}}
                                  title="Pubblica su Instagram"
                                >
                                  {publishing[post.id] === 'instagram' ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Instagram className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => publishPost(post.id, 'facebook')}
                                  disabled={publishing[post.id]}
                                  data-testid={`publish-facebook-btn-${post.id}`}
                                  className="py-2 px-3 rounded-lg text-sm font-medium text-white flex items-center gap-1 disabled:opacity-50 bg-blue-600 hover:bg-blue-700"
                                  title="Pubblica su Facebook"
                                >
                                  {publishing[post.id] === 'facebook' ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Facebook className="w-4 h-4" />
                                  )}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                        
                        <button
                          onClick={() => setEditingPost(post)}
                          data-testid={`edit-btn-${post.id}`}
                          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                          title="Modifica"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => deletePost(post.id)}
                          data-testid={`delete-btn-${post.id}`}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create New Post */}
      {activeTab === 'create' && (
        <CreatePostForm 
          onCreated={() => {
            setActiveTab('monthly');
            loadMonthlyPlan();
          }} 
        />
      )}

      {/* Preview Modal */}
      {previewPost && (
        <PostPreviewModal 
          post={previewPost} 
          onClose={() => setPreviewPost(null)}
          onPublish={(platform) => {
            publishPost(previewPost.id, platform);
            setPreviewPost(null);
          }}
          onGenerateImage={() => {
            generateImage(previewPost.id);
            setPreviewPost(null);
          }}
          generating={generating[previewPost.id]}
          publishing={publishing[previewPost.id]}
        />
      )}

      {/* Edit Modal */}
      {editingPost && (
        <EditPostModal 
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSave={(updates) => updatePost(editingPost.id, updates)}
        />
      )}
    </div>
  );
};

// Create Post Form Component
const CreatePostForm = ({ onCreated }) => {
  const [topic, setTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [postType, setPostType] = useState('post');
  const [creating, setCreating] = useState(false);

  const token = localStorage.getItem('token');

  const createPost = async () => {
    const finalTopic = topic === 'custom' ? customTopic : topic;
    if (!finalTopic) {
      toast.error('Seleziona un argomento');
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/social/create-post`, {
        topic: finalTopic,
        type: postType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.success) {
        toast.success('Post creato! Genera l\'immagine per pubblicarlo.');
        onCreated();
      } else {
        toast.error(res.data.error || 'Errore creazione');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione post');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-xl p-6 shadow-sm border">
        <h3 className="text-xl font-bold mb-6" style={{color: '#2A9FCB'}}>
          <Plus className="w-6 h-6 inline mr-2" />
          Crea Nuovo Post
        </h3>

        <div className="space-y-6">
          {/* Post Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
            <div className="flex gap-3">
              <button
                onClick={() => setPostType('post')}
                className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                  postType === 'post' 
                    ? 'border-sky-500 bg-sky-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Image className="w-6 h-6 mx-auto mb-1" style={{color: postType === 'post' ? '#2A9FCB' : '#9CA3AF'}} />
                <p className="font-medium">Post</p>
                <p className="text-xs text-gray-500">Immagine singola</p>
              </button>
              <button
                onClick={() => setPostType('reel')}
                className={`flex-1 py-3 px-4 rounded-xl border-2 transition-all ${
                  postType === 'reel' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Film className="w-6 h-6 mx-auto mb-1" style={{color: postType === 'reel' ? '#7C3AED' : '#9CA3AF'}} />
                <p className="font-medium">Reel</p>
                <p className="text-xs text-gray-500">Video breve</p>
              </button>
            </div>
          </div>

          {/* Topic Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Argomento Patologia (3 su 4 post)</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {PATOLOGIE.slice(0, 6).map(p => (
                <button
                  key={p.id}
                  onClick={() => setTopic(p.name)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    topic === p.name 
                      ? 'border-sky-500 bg-sky-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg mr-2">{p.icon}</span>
                  <span className="text-sm font-medium">{p.name}</span>
                </button>
              ))}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">Argomento Studio (1 su 4 post)</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {STUDIO_TOPICS.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTopic(t.name)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    topic === t.name 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-lg mr-2">{t.icon}</span>
                  <span className="text-sm font-medium">{t.name}</span>
                </button>
              ))}
            </div>

            {/* Custom Topic */}
            <div className="flex gap-2">
              <button
                onClick={() => setTopic('custom')}
                className={`px-4 py-2 rounded-lg border text-sm ${
                  topic === 'custom' 
                    ? 'border-sky-500 bg-sky-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                ✏️ Personalizzato
              </button>
              {topic === 'custom' && (
                <input
                  type="text"
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  placeholder="Inserisci argomento..."
                  className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500"
                />
              )}
            </div>
          </div>

          {/* Create Button */}
          <button
            onClick={createPost}
            disabled={creating || (!topic || (topic === 'custom' && !customTopic))}
            className="w-full py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            style={{background: 'linear-gradient(135deg, #2A9FCB, #1e7a9c)'}}
          >
            {creating ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            Crea Post
          </button>
        </div>
      </div>
    </div>
  );
};

// Post Preview Modal
const PostPreviewModal = ({ post, onClose, onPublish, onGenerateImage, generating, publishing }) => {
  const hasImage = post.image_url || post.public_url;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-lg">Anteprima Post</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4">
          {/* Image */}
          <div className="rounded-xl overflow-hidden mb-4 bg-gray-100">
            {hasImage ? (
              <img 
                src={post.public_url || (post.image_url?.startsWith('http') ? post.image_url : `${API}${post.image_url}`)}
                alt={post.topic_title}
                className="w-full aspect-square object-cover"
              />
            ) : (
              <div className="w-full aspect-square flex items-center justify-center">
                <div className="text-center">
                  <Image className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-3">Nessuna immagine</p>
                  <button
                    onClick={onGenerateImage}
                    disabled={generating}
                    className="px-4 py-2 rounded-lg text-white font-medium flex items-center gap-2 mx-auto"
                    style={{background: '#2A9FCB'}}
                  >
                    {generating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Genera Immagine
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Caption */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4 max-h-48 overflow-y-auto">
            <p className="whitespace-pre-wrap text-sm">{post.caption}</p>
          </div>

          {/* Info */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              post.type === 'reel' ? 'bg-purple-100 text-purple-700' : 'bg-sky-100 text-sky-700'
            }`}>
              {post.type === 'reel' ? '🎬 Reel' : '📷 Post'}
            </span>
            <span>{post.topic_title}</span>
          </div>

          {/* Publish Buttons */}
          {hasImage && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => onPublish('instagram')}
                disabled={publishing}
                className="py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                style={{background: 'linear-gradient(135deg, #E1306C, #C13584)'}}
              >
                {publishing === 'instagram' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Instagram className="w-5 h-5" />
                )}
                Pubblica IG
              </button>
              <button
                onClick={() => onPublish('facebook')}
                disabled={publishing}
                className="py-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 bg-blue-600 hover:bg-blue-700"
              >
                {publishing === 'facebook' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Facebook className="w-5 h-5" />
                )}
                Pubblica FB
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Edit Post Modal
const EditPostModal = ({ post, onClose, onSave }) => {
  const [caption, setCaption] = useState(post.caption || '');
  const [topicTitle, setTopicTitle] = useState(post.topic_title || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ caption, topic_title: topicTitle });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-lg">Modifica Post</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titolo</label>
            <input
              type="text"
              value={topicTitle}
              onChange={(e) => setTopicTitle(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={8}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-sky-500 resize-none"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2 rounded-lg text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              style={{background: '#2A9FCB'}}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaManager;
